import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme, withStyles, MenuItem as MuiMenuItem } from '@material-ui/core';
import { SelectProps, default as MaterialSelect } from '@material-ui/core/Select';
import { PaperProps } from '@material-ui/core/Paper';
import { MenuProps } from '@material-ui/core/Menu';
import { FormControl } from '@material-ui/core';
import { theme } from 'theme';
import colors from 'styles/variables/baseColors.module.scss';
import { changeLocale } from 'actions/I18n';
import { useDispatch } from 'react-redux';

export interface LanguageType {
  displayName: string;
  id: string;
}

const styles = () => ({
  root: {
    '&:hover': {
      backgroundColor: 'rgb(173,168,255, 0.25)'
    },
    '&.Mui-selected': {
      backgroundColor: `${colors.lavender} !important`
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${colors.lavender} !important`
    }
  }
});

const MenuItem = withStyles(styles())(MuiMenuItem);

export interface LanguageSwitcherProps extends SelectProps {
  handleSelect: (language: LanguageType) => void;
  languageId: string;
  languageOptions: LanguageType[];
  className?: string;
  classNameMenuItem?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    background: 'none',
    textAlign: 'left',
    color: colors.lifesize,
    fontSize: '1rem',
    maxWidth: 180,
    minWidth: 80,
    padding: '10px 0',
    textTransform: 'none',
    '&:before': {
      border: 'none !important'
    },
    '&:after': {
      border: 'none'
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 120
    },
    '& .MuiSelect-select': {
      marginRight: theme.spacing(1),
      '&:focus': {
        background: 'none'
      }
    },
    '& .MuiSelect-icon': {
      color: 'inherit'
    },
    '& .MuiMenuItem-root': {
      fontSize: '1em'
    }
  },
  formControl: {
    margin: 0,
    maxWidth: '500px'
  },
  menuItem: {
    fontSize: '1rem'
  }
}));
/**
 * Dropdown Component.
 * Below Props are required to Work this Dropdown component properly, otherwise it will have side effect such
 * as not able to update the state, label not displaying correctly, etc.
 * 1 . inputProps: it contains name and Id prop for the input component which will be generated by Material select
 *     name props and the state variable name should be same as it will help reduce redundant code.
 *     Please refer Dropdown.stories.tsx for more details.
 * @param props
 * @constructor
 */
export const LanguageSwitcher = (props: LanguageSwitcherProps) => {
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const { handleSelect, languageId, languageOptions, className, classNameMenuItem, ...otherProps } = props;

  const handleSelectLanguage = (selectedLanguage: any) => {
    const selectedOption = languageOptions.find((opt) => opt.id === selectedLanguage.target.value);
    if (selectedOption) {
      dispatch(changeLocale(selectedOption?.id, true, true));
      handleSelect(selectedOption);
    }
  };

  const paperProps: PaperProps = {
    elevation: 2,
    square: true
  };

  const menuProps: Partial<MenuProps> = {
    PaperProps: paperProps,
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center'
    }
  };

  return (
    <FormControl className={classes.formControl}>
      <MaterialSelect
        className={clsx(classes.select, className)}
        fullWidth={true}
        MenuProps={menuProps}
        onChange={handleSelectLanguage}
        value={languageId}
        {...otherProps}
      >
        {languageOptions.map((opt: LanguageType) => (
          <MenuItem key={opt.id} value={opt.id} className={clsx(classes.menuItem, classNameMenuItem)}>
            {opt.displayName}
          </MenuItem>
        ))}
      </MaterialSelect>
    </FormControl>
  );
};

export default LanguageSwitcher;
